import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Layers, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user, data.tenant);
      toast.success(`Welcome back, ${data.user.name}`);
      navigate(data.user.role === "master_admin" ? "/master" : "/jobs");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7] px-5">
      <div className="absolute top-0 left-0 w-full h-72 bg-gradient-to-b from-[#007AFF]/[0.06] to-transparent pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-sm"
      >
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#007AFF] flex items-center justify-center shadow-lg shadow-[#007AFF]/20 mb-4">
            <Layers size={26} className="text-white" strokeWidth={2} />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight">HireScope</h1>
          <p className="text-[#86868B] text-sm mt-1">AI-scored hiring, beautifully simple.</p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.05)] border border-black/5 p-7 space-y-4">
          <div>
            <label className="text-sm font-semibold text-[#1D1D1F]">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="mt-1.5 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors"
              placeholder="you@company.com"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-[#1D1D1F]">Password</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="mt-1.5 w-full bg-gray-50/70 border border-black/10 rounded-xl px-4 py-3 text-sm focus:bg-white focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF] outline-none transition-colors"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit" disabled={loading} data-testid="login-submit"
            className="w-full bg-[#007AFF] text-white rounded-full px-6 py-3 font-semibold text-sm hover:bg-[#0051FF] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign in
          </button>
        </form>

        <p className="text-center text-xs text-[#6E6E73] mt-6" data-testid="login-footer">
          Powered by{" "}
          <a href="https://oslolabs.tech" target="_blank" rel="noreferrer"
            className="font-semibold text-[#515154] hover:text-[#007AFF] transition-colors" data-testid="oslo-labs-link">
            OSLO LABS
          </a>
        </p>
      </motion.div>
    </div>
  );
}
